import React from 'react';
import { motion } from 'framer-motion';
import { Lightbulb, FileSignature, Ear, UserCheck } from 'lucide-react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

const pairs = [
  {
    icon: Lightbulb,
    ai: 'AI suggests',
    you: 'You decide',
    description: 'Risk flags and evidence-based prompts surface in real time — your clinical judgment makes the call.',
  },
  {
    icon: FileSignature,
    ai: 'AI drafts',
    you: 'You sign',
    description: 'Every SOAP / DAP / BIRP note is drafted from the session, then reviewed and signed by you.',
  },
  {
    icon: Ear,
    ai: 'AI listens',
    you: 'You lead',
    description: 'The platform supports the work; the therapeutic relationship is yours. Always.',
  },
];

export default function HumanInTheLoop() {
  const { ref, inView } = useScrollAnimation();

  return (
    <section
      ref={ref}
      className="relative section-padding overflow-hidden bg-gradient-to-br from-brand-950 via-brand-900 to-brand-950 text-white"
    >
      {/* Background accents */}
      <div className="absolute inset-0 opacity-30 pointer-events-none">
        <div className="absolute top-1/4 -left-32 w-96 h-96 rounded-full bg-accent-500/30 blur-3xl" />
        <div className="absolute bottom-1/4 -right-32 w-96 h-96 rounded-full bg-brand-500/40 blur-3xl" />
      </div>

      <div className="relative container-max">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto mb-12"
        >
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider bg-white/10 text-accent-300 border border-white/10 backdrop-blur-sm mb-5">
            <UserCheck className="w-3.5 h-3.5" />
            Human in the loop
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight mb-5 text-balance">
            Doctorite empowers providers.{' '}
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-accent-400 to-accent-300">
              It never replaces them.
            </span>
          </h2>
          <p className="text-lg text-brand-200/90 leading-relaxed">
            Therapy is one of the most human professions there is. Our AI exists to lift the busywork off your plate — so you can do more of what only a clinician can do.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-5 max-w-5xl mx-auto">
          {pairs.map((pair, idx) => (
            <motion.div
              key={pair.ai}
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: idx * 0.1 }}
              className="p-6 sm:p-7 rounded-2xl bg-white/5 backdrop-blur-sm border border-white/10 hover:border-white/20 hover:bg-white/10 transition-all"
            >
              <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-accent-500 to-accent-600 flex items-center justify-center mb-5">
                <pair.icon className="w-5 h-5 text-white" />
              </div>
              <div className="flex items-baseline gap-2 mb-3">
                <span className="text-sm font-medium text-brand-300/70">{pair.ai}</span>
                <span className="text-brand-300/40">→</span>
                <span className="text-base font-bold text-white">{pair.you}</span>
              </div>
              <p className="text-sm text-brand-200/80 leading-relaxed">{pair.description}</p>
            </motion.div>
          ))}
        </div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : {}}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="text-center mt-10 text-sm text-brand-200/70 italic"
        >
          Every clinical decision, every signature, every word that reaches a client — yours.
        </motion.p>
      </div>
    </section>
  );
}
